import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FaUmbrellaBeach } from 'react-icons/fa';

import { apiService } from '../../components/Config/api.service';
import LoadingBarWithStyles from '../../components/Utils/LoadingBarWithStyles';
import { signalerErreur } from '../../components/Utils/signalerErreur';

/**
 * Solde de congés sur « Mes congés » (bolt 044, Yros/Kavero#331).
 *
 * Aucun contrat nouveau : consommation pure de C10 (`GET /api/rh/solde`) via
 * `apiService.listerSoldeRh`, déjà présent. Sans `employee_id` en main ici
 * (cet écran ne reçoit aucun `employee` en prop), l'appel se fait sans
 * argument — le contrat sert alors le demandeur par défaut.
 *
 * `rafraichir` est le même compteur que celui poussé à `MesDemandes` : un
 * dépôt réussi fait bouger `restant_projete` aussi, puisqu'il compte les
 * demandes en attente.
 *
 * YRH-FOUND-019 (Yros/Kavero#418) : le solde vient de la Paie. L'écran
 * montre le restant, et à part le restant après demandes en attente ; un
 * restant négatif reste négatif, et un avertissement — jamais un blocage —
 * dit quand les demandes en attente dépassent le solde. Sans solde (pas de
 * contrat en cours), le message de la Paie s'affiche tel quel.
 *
 * Le projeté replie sur le restant quand il manque. La coalescence se fait
 * **avant** la conversion — `Number(x ?? y ?? 0)` — jamais
 * `Number(x) ?? Number(y)` : `Number(undefined)` vaut `NaN`, et `NaN ?? z`
 * reste `NaN`, si bien que ce repli ne se déclencherait jamais.
 */
function SoldeConges({ rafraichir = 0 }) {
  const { t } = useTranslation();
  const [soldes, setSoldes] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [message, setMessage] = useState(null);

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur(null);
    setMessage(null);
    try {
      const reponse = await apiService.listerSoldeRh();
      const liste = reponse?.ok && Array.isArray(reponse.soldes) ? reponse.soldes : [];
      setSoldes(liste);
      // Refus lisible de la Paie (pas de contrat en cours…) : affiché tel quel.
      if (reponse?.ok === false && reponse.message) setMessage(reponse.message);
    } catch (e) {
      signalerErreur(e, 'SoldeConges.charger');
      setSoldes([]);
      setErreur(t('soldeConges.erreurGenerique'));
    } finally {
      setChargement(false);
    }
  }, [t]);

  useEffect(() => {
    charger();
  }, [charger, rafraichir]);

  return (
    <div className="bg-white rounded-2xl p-4 sm:p-5 shadow-sm border border-slate-100">
      <div className="flex items-center gap-2 mb-3.5 pb-2.5 border-b border-slate-100">
        <span className="w-8 h-8 rounded-xl bg-teal-500/10 text-teal-600 flex items-center justify-center flex-shrink-0">
          <FaUmbrellaBeach className="w-4 h-4" />
        </span>
        <h3 className="text-sm sm:text-base font-bold text-slate-800 tracking-tight">
          {t('soldeConges.titre')}
        </h3>
      </div>

      {erreur && (
        <p
          data-testid="solde-conges-erreur"
          className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
        >
          {erreur}
        </p>
      )}

      {chargement ? (
        <div data-testid="solde-conges-chargement">
          <LoadingBarWithStyles />
        </div>
      ) : erreur ? null : message ? (
        <p data-testid="solde-conges-message" className="py-6 text-center text-sm text-gray-600">
          {message}
        </p>
      ) : soldes.length === 0 ? (
        <p
          data-testid="solde-conges-vide"
          className="py-8 text-center text-sm text-gray-500"
        >
          {t('soldeConges.vide')}
        </p>
      ) : (
        <div className="space-y-1">
          {soldes.map((solde) => {
            // Une ligne de la Paie n'a pas de type de congé.
            const cle = solde.type_conge_id ?? 'paie';
            // Coalescence avant conversion : voir le commentaire d'en-tête.
            const restant = Number(solde.restant ?? 0);
            const restantProjete = Number(solde.restant_projete ?? solde.restant ?? 0);

            return (
              <div
                key={cle}
                data-testid={`solde-conges-ligne-${cle}`}
                className="py-2 border-b border-slate-100 last:border-0"
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-700">{solde.libelle}</span>
                  <span
                    data-testid={`solde-conges-restant-${cle}`}
                    className={`text-sm font-semibold ${restant < 0 ? 'text-red-700' : 'text-slate-800'}`}
                  >
                    {t('soldeConges.joursRestants', { count: restant })}
                  </span>
                </div>
                <p
                  data-testid={`solde-conges-projete-${cle}`}
                  className="text-xs text-slate-500 text-right mt-0.5"
                >
                  {t('soldeConges.apresDemandes', { count: restantProjete })}
                </p>
                {restantProjete < 0 && (
                  <p
                    data-testid="solde-conges-depassement"
                    className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2"
                  >
                    {t('soldeConges.depassement')}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default SoldeConges;
